import { baseService } from "src/store/base.store";
import { Promoter, PromotersFilter } from "./types/promoter.entity";
import { PromoCode, PromoCodesFilter } from "./types/promo-code.entity";
import { promoterRepository } from "./promoter.repository";
import { promoCodeRepository } from "./promo-code.repository";
import { FilterQuery } from "src/firebase/firestore/types";
import { callFunctionSafe } from "src/firebase/functions";
import { TopUpType } from "./types/promo-code.entity";
// Types for callable functions
export type AddPromoterRequest = {
  name: string;
  email?: string;
  commissionRate: number;
};

export type AddPromoterResponse = {
  success: boolean;
  error?: string;
};

export type UpdatePromoterPayload = {
  name?: string;
  email?: string;
  commissionRate?: number;
};

export type UpdatePromoterResponse = {
  success: boolean;
  error?: string;
};

export type GeneratePromoCodePayload = {
  code: string;
  promoterId: string;
  bonusPercentage: number;
  active: boolean;
  usageLimit?: number;
  expiryDate: Date;
  minimumPurchaseAmount?: number;
  appliedTo?: TopUpType[];
};

export type GeneratePromoCodeResponse = {
  success: boolean;
  error?: string;
};

export type UpdatePromoCodePayload = {
  active?: boolean;
  bonusPercentage?: number;
  usageLimit?: number;
  expiryDate?: string;
  minimumPurchaseAmount?: number;
  appliedTo?: TopUpType[];
};

export type UpdatePromoCodeResponse = {
  success: boolean;
  error?: string;
};

export const buildPromoterWhere = async (
  where: PromotersFilter
): Promise<FilterQuery<Promoter>["where"]> => {
  const q: any = {}; // Using any to handle nested field notation
  
  // Note: id cannot be queried directly in Firestore where clauses
  // It will be handled separately in the query functions
  
  // For now, we'll handle text searches client-side to avoid complex Firestore indexes
  // This allows for case-insensitive partial matching
  
  return q;
};



export const promoterService = baseService.injectEndpoints({
  endpoints: (builder) => ({
    getPromoters: builder.query<Promoter[], { where?: PromotersFilter; offset?: string }>({
      keepUnusedDataFor: 0, // Disable caching for debugging
      queryFn: async ({ where, offset }) => {
        try {
          // Handle id search separately (direct document lookup)
          if (where?.id) {
            try {
              const promoter = await promoterRepository.findById(where.id);
              return { data: promoter ? [promoter] : [] };
            } catch (error) {
              console.error("Error finding promoter by ID:", error);
              return { error: { status: 500, data: error } };
            }
          }
          
          const filterQuery = await buildPromoterWhere(where || {});
          
          // Check if we have search filters (text searches)
          const hasTextFilters = where && (where.name || where.email);
          const queryOptions: any = { 
            where: filterQuery, 
            offset, 
            limit: hasTextFilters ? 50 : 10 // Get more results for client-side filtering
          };
          
          // Only add orderBy when not filtering to avoid complex index requirements
          if (!hasTextFilters) {
            queryOptions.orderBy = ["createdAt", "desc"];
          }
          
          let promoters = await promoterRepository.find(queryOptions);
          
          // Apply client-side filtering for case-insensitive partial matching
          if (hasTextFilters && where) {
            promoters = promoters.filter(promoter => {
              let matches = true;
              
              if (where.name) {
                const searchTerm = where.name.toLowerCase();
                const promoterName = (promoter.name || '').toLowerCase();
                matches = matches && promoterName.includes(searchTerm);
              }
              
              if (where.email) {
                const searchTerm = where.email.toLowerCase();
                const promoterEmail = (promoter.email || '').toLowerCase();
                matches = matches && promoterEmail.includes(searchTerm);
              }
              
              return matches;
            });
            
            // Limit to 10 results after filtering
            promoters = promoters.slice(0, 10);
          }
          
          return { data: promoters };
        } catch (error) {
          console.error("Error in getPromoters:", error);
          return { error: { status: 500, data: error } };
        }
      },
    }),

    getPromoterById: builder.query<Promoter, string>({
      queryFn: async (promoterId) => {
        try {
          const promoter = await promoterRepository.findById(promoterId);
          if (!promoter) {
            return { error: { status: 404, data: "Promoter not found" } };
          }
          return { data: promoter };
        } catch (error) {
          console.error("Error in getPromoterById:", error);
          return { error: { status: 500, data: error } };
        }
      },
    }),

    countPromoters: builder.query<number, { where?: PromotersFilter }>({
      queryFn: async ({ where }) => {
        try {
          // Handle id search separately (direct document lookup)
          if (where?.id) {
            try {
              const promoter = await promoterRepository.findById(where.id);
              return { data: promoter ? 1 : 0 };
            } catch (error) {
              console.error("Error finding promoter by ID:", error);
              return { data: 0 };
            }
          }
          
          const filterQuery = await buildPromoterWhere(where || {});
          const count = await promoterRepository.count(filterQuery);
          return { data: count };
        } catch (error) {
          console.error("Error in countPromoters:", error);
          return { error: { status: 500, data: error } };
        }
      },
    }),

    createPromoter: builder.mutation<AddPromoterResponse, AddPromoterRequest>({
      queryFn: async (promoterData) => {
        try {
          const result = await callFunctionSafe<AddPromoterResponse>('addPromoter', promoterData);
          
          if ('data' in result && result.data.success) {
            return { data: { success: true } };
          } else {
            // Handle error from the callable function or Firebase error
            let errorMessage = 'Failed to create promoter. Please try again.';
            
            if ('data' in result && result.data.error) {
              errorMessage = result.data.error;
            } else if ('error' in result) {
              errorMessage = result.error;
            }
            
            return { error: { status: 500, data: errorMessage } };
          }
        } catch (error) {
          console.error('Error calling addPromoter:', error);
          return { error: { status: 500, data: 'Failed to create promoter. Please try again.' } };
        }
      },
    }),
    updatePromoterById: builder.mutation<UpdatePromoterResponse, { promoterId: string; data: UpdatePromoterPayload }>({
      queryFn: async ({ promoterId, data }) => {
        try {
          const result = await callFunctionSafe<UpdatePromoterResponse>('updatePromoterById', {
            promoterId,
            ...data
          });
          
          if ('data' in result && result.data.success) {
            return { data: { success: true } };
          } else {
            // Handle error from the callable function or Firebase error
            let errorMessage = 'Failed to update promoter. Please try again.';
            
            if ('data' in result && result.data.error) {
              errorMessage = result.data.error;
            } else if ('error' in result) {
              errorMessage = result.error;
            }
            
            return { error: { status: 500, data: errorMessage } };
          }
        } catch (error) {
          console.error('Error calling updatePromoterById:', error);
          return { error: { status: 500, data: 'Failed to update promoter. Please try again.' } };
        }
      },
    }),

    generatePromoCode: builder.mutation<GeneratePromoCodeResponse, GeneratePromoCodePayload>({
      queryFn: async (promoCodeData) => {
        try {
          const result = await callFunctionSafe<GeneratePromoCodeResponse>('generatePromoCode', promoCodeData);
          
          if ('data' in result && result.data.success) {
            return { data: { success: true } };
          } else {
            // Handle error from the callable function or Firebase error
            let errorMessage = 'Failed to generate promo code. Please try again.';
            
            if ('data' in result && result.data.error) {
              errorMessage = result.data.error;
            } else if ('error' in result) {
              errorMessage = result.error;
            }
            
            return { error: { status: 500, data: errorMessage } };
          }
        } catch (error) {
          console.error('Error calling generatePromoCode:', error);
          return { error: { status: 500, data: 'Failed to generate promo code. Please try again.' } };
        }
      },
    }),

    updatePromoCodeById: builder.mutation<UpdatePromoCodeResponse, { promoCodeId: string; data: UpdatePromoCodePayload }>({
      queryFn: async ({ promoCodeId, data }) => {
        try {
          const result = await callFunctionSafe<UpdatePromoCodeResponse>('updatePromoCodeById', {
            promoCodeId,
            ...data
          });
          
          if ('data' in result && result.data.success) {
            return { data: { success: true } };
          } else {
            // Handle error from the callable function or Firebase error
            let errorMessage = 'Failed to update promo code. Please try again.';
            
            if ('data' in result && result.data.error) {
              errorMessage = result.data.error;
            } else if ('error' in result) {
              errorMessage = result.error;
            }
            
            return { error: { status: 500, data: errorMessage } };
          }
        } catch (error) {
          console.error('Error calling updatePromoCodeById:', error);
          return { error: { status: 500, data: 'Failed to update promo code. Please try again.' } };
        }
      },
    }),

    deletePromoter: builder.mutation<void, string>({
      queryFn: async (promoterId) => {
        try {
          const response = await callFunctionSafe<{ success: boolean; error?: string }>('deletePromoterById', { promoterId });
          
          if ('error' in response) {
            return { error: { status: 500, data: response.error } };
          }
          
          if (!response.data?.success) {
            return { error: { status: 400, data: response.data?.error || 'Failed to delete promoter' } };
          }
          
          return { data: undefined };
        } catch (error) {
          console.error("Error in deletePromoter:", error);
          return { error: { status: 500, data: error } };
        }
      },
    }),

    getPromoCodesByPromoterId: builder.query<PromoCode[], { promoterId: string; filter?: PromoCodesFilter }>({
      queryFn: async ({ promoterId, filter = {} }) => {
        try {
          const where = {
            promoterId,
            ...filter
          };
          
          const promoCodes = await promoCodeRepository.find({
            where,
            orderBy: ["createdAt", "desc"],
            limit: 50
          });
          
          return { data: promoCodes };
        } catch (error) {
          console.error("Error in getPromoCodesByPromoterId:", error);
          return { error: { status: 500, data: error } };
        }
      },
    }),
  }),
});

export const {
  useGetPromotersQuery,
  useGetPromoterByIdQuery,
  useCountPromotersQuery,
  useCreatePromoterMutation,
  useUpdatePromoterByIdMutation,
  useDeletePromoterMutation,
  useGeneratePromoCodeMutation,
  useUpdatePromoCodeByIdMutation,
  useGetPromoCodesByPromoterIdQuery,
} = promoterService;
